/*
 * Copyright (c) CIRROM 2025.
 */

import React from "react";
import { Typography, CardHeader } from "@mui/material";
import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";

import styled from "@emotion/styled";
import { MiraCard, MiraCardContent } from "../mira/components/common/Card";
import "chart.js/auto";
import { Doughnut } from "react-chartjs-2";

const GaugeContainer = styled.div`
  height: 200px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  padding-bottom: 20px;
`;

const GaugeInner = styled.div`
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
  z-index: 10;
`;

const LegendContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;
  justify-content: center;
`;

const LegendItem = styled.div<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  opacity: ${({ isActive }) => (isActive ? 1 : 0.5)};
  font-size: 0.75rem;
`;

const LegendColorBox = styled.div<{ color: string }>`
  width: 10px;
  height: 10px;
  background-color: ${({ color }) => color};
  border-radius: 2px;
  flex-shrink: 0;
`;

interface DashboardHealthScoreProps {
  scoreStats: any;
}

export const DashboardHealthScore: React.FC<DashboardHealthScoreProps> = ({
  scoreStats,
}) => {
  const latestScore = scoreStats?.latest_stats?.score || 0;
  const startScore = scoreStats?.start_stats?.score || 0;

  const bands = [
    { min: 0, max: 35, color: "#e50144", label: "Critical" },
    { min: 35, max: 45, color: "#e65100", label: "Poor" },
    { min: 45, max: 55, color: "#f57c00", label: "Fair" },
    { min: 55, max: 65, color: "#ffa000", label: "Average" },
    { min: 65, max: 76, color: "#fbc02d", label: "Good" },
    { min: 76, max: 86, color: "#b5c332", label: "Great" },
    { min: 86, max: 92, color: "#8bc34a", label: "Excellent" },
    { min: 92, max: 100, color: "#34be5d", label: "Outstanding" },
  ];

  // Find current band
  const currentBand =
    bands.find((band) => latestScore >= band.min && latestScore <= band.max) ||
    bands[0];

  const createGaugeData = () => {
    // Create segments for each band
    const data = bands.map((band) => band.max - band.min);
    const colors = bands.map((band) => band.color);

    return {
      datasets: [
        {
          data,
          backgroundColor: colors,
          borderWidth: 1,
          borderColor: "#fff",
        },
      ],
    };
  };

  // Calculate needle rotation based on score
  const calculateNeedleRotation = () => {
    const minScore = 0;
    const maxScore = 100;
    const scoreRange = maxScore - minScore;
    const normalizedScore = (latestScore - minScore) / scoreRange;
    return normalizedScore * 180 - 90; // -90 to 90 degrees for semicircle
  };

  const gaugeOptions = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    circumference: 180, // Semicircle
    rotation: 270, // Start from left
    cutout: "75%",
    layout: {
      padding: 0,
    },
  };

  return (
    <MiraCard style={{ height: "100%" }}>
      <CardHeader title={"Health Score"} />
      <MiraCardContent>
        <GaugeContainer>
          <Doughnut data={createGaugeData()} options={gaugeOptions} />
          <div
            style={{
              position: "absolute",
              left: "50%",
              bottom: "28px",
              width: "3px",
              height: "70px",
              backgroundColor: "#2c3e50",
              transformOrigin: "bottom center",
              transform: `translateX(-50%) rotate(${calculateNeedleRotation()}deg)`,
              zIndex: 5,
              borderRadius: "2px",
            }}
          />

          <div
            style={{
              position: "absolute",
              left: "50%",
              width: "14px",
              height: "14px",
              backgroundColor: "#2c3e50",
              borderRadius: "50%",
              transform: "translateX(-50%)",
              zIndex: 6,
              border: "2px solid #fff",
            }}
          />

          <GaugeInner>
            <Typography
              variant="h2"
              color={"textPrimary"}
              style={{
                fontSize: "3rem",
                fontWeight: "bold",
                margin: 0,
              }}
            >
              {latestScore}
            </Typography>
            <Typography
              variant="body1"
              style={{
                color: currentBand.color,
                fontWeight: "bold",
                fontSize: "1.1rem",
                marginTop: "4px",
              }}
            >
              {currentBand.label}
            </Typography>
          </GaugeInner>
        </GaugeContainer>

        <LegendContainer>
          {bands.map((band, index) => {
            const isActive = latestScore >= band.min && latestScore <= band.max;

            return (
              <LegendItem key={index} isActive={isActive}>
                <LegendColorBox color={band.color} />
                <span style={{ fontWeight: isActive ? "bold" : "normal" }}>
                  {band.label}
                </span>
              </LegendItem>
            );
          })}
        </LegendContainer>

        <Table size={"small"} style={{ marginTop: "16px" }}>
          <TableBody>
            <TableRow>
              <TableCell>
                <Typography variant={"caption"} style={{ fontWeight: "bold" }}>
                  Start
                </Typography>
              </TableCell>
              <TableCell>{startScore}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography variant={"caption"} style={{ fontWeight: "bold" }}>
                  Today
                </Typography>
              </TableCell>
              <TableCell>{latestScore}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </MiraCardContent>
    </MiraCard>
  );
};
